/**
 * FeedTile — siste innslag i familieflyten på Hjem (Fase 5; ny stil i #154).
 *
 * The feed has its own /familie route (not a bottom-bar tab), so this row is
 * its primary entry point. #154: en glassrad med avataren til den som står
 * bak innslaget, selve setningen (som i FeedCard), «for 2 timer siden ·
 * familieflyten» og en pil. Hele raden lenker til /familie.
 *
 * Uten innslag (eller uten økt, der /api/feed svarer 401) står «Familieflyt»
 * med «Ingen hendelser ennå» under, og ingen avatar.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../../components/primitives/Card';
import { Icon } from '../../../components/primitives/Icon';
import { InitialAvatar, asColorSlot } from '../../../components/primitives/InitialAvatar';
import { formatDateRelative } from '../../../components/primitives/RelativeTime';
import { feedMessage } from '../../feed/FeedCard';
import { useFeed } from '../../feed/useFeedQuery';

export function FeedTile(): ReactNode {
  const { t } = useTranslation();
  const feed = useFeed();
  const latest = feed.data?.[0];

  return (
    <Link
      to="/familie"
      data-testid="feed-tile"
      className={`${GLASS_SURFACE} flex min-h-11 items-center gap-3 rounded-row py-2.5 pr-3.5 pl-2.5 text-ink shadow-md`}
    >
      {latest ? (
        <InitialAvatar
          name={latest.actor_short_name}
          colorSlot={asColorSlot(latest.actor_color_token)}
          size={40}
          decorative
        />
      ) : null}
      <span className="min-w-0 flex-1">
        <span data-testid="feed-tile-headline" className="block text-sm font-extrabold">
          {latest ? feedMessage(latest, t) : t('feed.title')}
        </span>
        <span data-testid="feed-tile-subline" className="block text-caption text-ink-2">
          {latest
            ? t('home.feed.meta', { time: formatDateRelative(latest.created_at) })
            : t('feed.tile.empty')}
        </span>
      </span>
      <Icon.ChevR size={20} className="shrink-0" />
    </Link>
  );
}
